import { Component, ErrorInfo, ReactNode } from 'react';

import { addError } from '../../utils/datadog';

interface SpotlightErrorBoundaryProps {
	children: ReactNode;
}

interface SpotlightErrorBoundaryState {
	hasError: boolean;
}

/**
 * Error boundary for Spotlight components that automatically tags errors
 * with domain and code owner context for DataDog RUM monitoring.
 *
 * Usage:
 * ```tsx
 * <SpotlightErrorBoundary>
 *   <UserSpotlightViewer />
 * </SpotlightErrorBoundary>
 * ```
 *
 * Errors will be tagged with:
 * - domain: 'Spotlight'
 * - code_owner: '@bonusly/bu-insights'
 *
 * Filter in DataDog RUM:
 * - @context.domain:Spotlight
 * - @context.code_owner:@bonusly/bu-insights
 */
export class SpotlightErrorBoundary extends Component<SpotlightErrorBoundaryProps, SpotlightErrorBoundaryState> {
	constructor(props: SpotlightErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		addError(error, {
			domain: 'Spotlight',
			code_owner: '@bonusly/bu-insights',
			component_stack: errorInfo.componentStack,
		});
	}

	static getDerivedStateFromError(): SpotlightErrorBoundaryState {
		return { hasError: true };
	}

	render() {
		if (this.state.hasError) {
			return null;
		}
		return this.props.children;
	}
}
